import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import GlobalFooter from '../components/GlobalFooter';
import {
  ArrowLeft, Pill, AlertTriangle, Clock,
  Shield, Brain, Activity, Heart, Zap, CheckCircle2,
  Info, Utensils, Dumbbell,
  BookOpen, FlaskConical, Stethoscope, Droplets
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../components/ui/accordion';

const IBS_PROTOCOL = [
  {
    name: 'Probiotics (Multi-Strain)',
    dosage: '20-50 billion CFU daily',
    timing: 'Morning on empty stomach, 30 min before food',
    icon: FlaskConical,
    color: 'bg-teal-100 text-teal-700 border-teal-200',
    evidence: 'A',
    why: 'The strongest evidence for any IBS supplement. Multiple meta-analyses show probiotics significantly reduce overall IBS symptoms including bloating, pain, and altered bowel habits. They restore gut microbiome balance disrupted in IBS. Certain strains target specific symptoms: B. infantis 35624 for overall IBS, L. plantarum 299v for bloating and pain.',
    interactions: ['Safe with most IBS medications', 'Separate from antibiotics by 2+ hours', 'Immunosuppressed patients should consult doctor first'],
    notes: 'Strain-specific: B. infantis 35624 (Align) has the most IBS-specific evidence. Multi-strain formulas work better than single strains. Give 4-6 weeks for full benefit. Refrigerated brands maintain potency.'
  },
  {
    name: 'Peppermint Oil (Enteric-Coated)',
    dosage: '180-200mg, 2-3 times daily',
    timing: '30-60 minutes before meals',
    icon: Zap,
    color: 'bg-green-100 text-green-700 border-green-200',
    evidence: 'A',
    why: 'Peppermint oil is an antispasmodic that relaxes smooth muscle in the gut, reducing cramping and pain. Meta-analyses of 12+ RCTs show it is significantly more effective than placebo for IBS pain and global symptoms. The NNT (number needed to treat) is just 3 — meaning 1 in 3 patients gets substantial relief.',
    interactions: ['Enteric coating is essential — non-coated causes heartburn/reflux', 'May interact with acid-reducing medications (can dissolve coating prematurely)', 'Safe with antidepressants used for IBS (amitriptyline, SSRIs)'],
    notes: 'MUST be enteric-coated to survive stomach acid and release in the intestines. IBgard and Heather\'s Tummy Tamers are well-studied brands. Take BEFORE meals, not after.'
  },
  {
    name: 'L-Glutamine',
    dosage: '5-10g daily',
    timing: 'On empty stomach, morning or between meals',
    icon: Shield,
    color: 'bg-violet-100 text-violet-700 border-violet-200',
    evidence: 'B+',
    why: 'L-Glutamine is the primary fuel source for intestinal cells (enterocytes) and helps repair and maintain the gut lining. It reduces intestinal permeability ("leaky gut") — a factor in post-infectious and diarrhea-predominant IBS. A 2019 RCT showed significant improvement in IBS-D symptoms.',
    interactions: ['Safe with all IBS medications', 'Avoid in liver disease or hepatic encephalopathy', 'Safe long-term at recommended doses'],
    notes: 'Particularly effective for IBS-D (diarrhea-predominant) and post-infectious IBS. Powder form mixed in water is most practical at higher doses. Start with 5g and increase.'
  },
  {
    name: 'Psyllium Husk (Soluble Fiber)',
    dosage: '3-6g daily (start low, increase gradually)',
    timing: 'With meals, with plenty of water',
    icon: Utensils,
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    evidence: 'B+',
    why: 'Soluble fiber (NOT insoluble) is the recommended fiber type for IBS. Psyllium normalizes bowel habits in both IBS-C and IBS-D by absorbing water and forming a gel that regulates transit time. Multiple RCTs show significant improvement in overall IBS symptoms vs placebo.',
    interactions: ['Take medications 1 hour before or 2 hours after psyllium (can slow absorption)', 'Increase water intake — psyllium absorbs water', 'Safe with all IBS medications'],
    notes: 'START SLOW — too much fiber too fast worsens bloating. Begin with 1-2g/day, increase by 1g every 3-5 days. SOLUBLE fiber (psyllium) helps IBS; INSOLUBLE fiber (wheat bran) often worsens it.'
  },
  {
    name: 'Digestive Enzymes',
    dosage: 'Broad-spectrum enzyme complex with each meal',
    timing: 'At the beginning of meals',
    icon: Activity,
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    evidence: 'B',
    why: 'Many IBS patients have reduced digestive enzyme production, leading to incomplete digestion that triggers gas, bloating, and pain. Supplemental enzymes (lipase, protease, amylase, lactase) help break down food more completely. Particularly helpful if symptoms worsen after meals.',
    interactions: ['Safe with all IBS medications', 'May reduce effectiveness of certain oral medications if taken simultaneously', 'Consider separate lactase if lactose-intolerant'],
    notes: 'Look for a broad-spectrum formula with DPP-IV (helps digest gluten traces), lactase, and alpha-galactosidase (for beans/cruciferous). Take WITH food, not on empty stomach.'
  },
  {
    name: 'Magnesium Glycinate / Citrate',
    dosage: '200-400mg daily',
    timing: 'Evening or before bed',
    icon: Heart,
    color: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    evidence: 'B',
    why: 'Magnesium relaxes smooth muscle in the GI tract, reducing spasms and cramping. Glycinate is calming and well-absorbed (better for IBS-D), while citrate has a mild osmotic laxative effect (better for IBS-C). Also helps with stress and sleep — both major IBS triggers.',
    interactions: ['Separate from antibiotics by 2+ hours', 'Safe with all IBS medications', 'Citrate form may increase bowel movements — beneficial for IBS-C'],
    notes: 'Choose your form based on IBS type: Glycinate for IBS-D (calming, no laxative effect), Citrate for IBS-C (gentle motility support). Both help with stress, which drives IBS flares.'
  },
  {
    name: 'Ginger Extract',
    dosage: '250-500mg standardized extract, 2-3 times daily',
    timing: 'Before meals',
    icon: Utensils,
    color: 'bg-rose-100 text-rose-700 border-rose-200',
    evidence: 'B',
    why: 'Ginger is a prokinetic — it accelerates gastric emptying, reducing bloating and nausea. It also has anti-inflammatory and antispasmodic effects in the gut. Studies show it reduces nausea, bloating, and pain in functional GI disorders. Particularly helpful when bloating is a dominant symptom.',
    interactions: ['May enhance blood thinners slightly', 'Safe with all IBS medications', 'May lower blood sugar mildly'],
    notes: 'Standardized extract (to gingerols) is more effective than raw ginger. Can also use fresh ginger tea between meals. Anti-nausea effects are well-established.'
  },
  {
    name: 'Vitamin D3',
    dosage: '2,000-5,000 IU daily',
    timing: 'Morning with a fat-containing meal',
    icon: Zap,
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    evidence: 'B',
    why: 'IBS patients have significantly higher rates of Vitamin D deficiency. Multiple RCTs show D3 supplementation improves overall IBS symptom severity, quality of life, and reduces anxiety/depression scores (common IBS comorbidities). It also modulates gut immune function and the microbiome.',
    interactions: ['Safe with all IBS medications', 'Enhances calcium absorption', 'Safe long-term at recommended doses'],
    notes: 'Get levels tested — aim for 40-60 ng/mL. Correction of deficiency alone often meaningfully improves IBS symptoms. Many gastroenterologists now routinely test D3 in IBS patients.'
  }
];

const COMMON_MEDICATIONS = [
  { name: 'Antispasmodics (Bentyl, Buscopan)', notes: 'All supplements are safe. Peppermint oil and magnesium have complementary antispasmodic effects — may allow lower medication doses over time.' },
  { name: 'Low-Dose Antidepressants (Amitriptyline, SSRIs)', notes: 'All supplements are safe. Probiotics and vitamin D may enhance the mood benefits. L-glutamine and fiber have no interactions.' },
  { name: 'Rifaximin (Xifaxan)', notes: 'Separate probiotics from rifaximin by 2+ hours. After completing a rifaximin course, probiotics are especially important to restore beneficial bacteria.' },
  { name: 'Loperamide (Imodium)', notes: 'Safe with all supplements. Psyllium (soluble fiber) may reduce need for loperamide by normalizing stool consistency. Probiotics complement the approach.' },
  { name: 'Linaclotide (Linzess) / Lubiprostone (Amitiza)', notes: 'Safe with all supplements. For IBS-C patients, magnesium citrate adds gentle osmotic support. Psyllium fiber helps normalize motility.' },
  { name: 'Eluxadoline (Viberzi)', notes: 'Safe with all supplements. Avoid excessive peppermint oil (theoretical concern with bile acid metabolism). All others are fine.' }
];

const LIFESTYLE_TIPS = [
  {
    icon: Utensils,
    title: 'Low-FODMAP Diet',
    content: 'The Low-FODMAP diet is the most evidence-based dietary approach for IBS (75% response rate). Work with a dietitian for the 3-phase approach: elimination (2-6 weeks), reintroduction (6-8 weeks), and personalization. Common triggers: garlic, onion, wheat, dairy, certain fruits.'
  },
  {
    icon: Brain,
    title: 'Gut-Brain Axis Management',
    content: 'IBS is a disorder of gut-brain interaction. Gut-directed hypnotherapy has a 70-80% response rate (comparable to medication). CBT, mindfulness meditation, and diaphragmatic breathing (4-7-8 technique) all reduce symptom severity. The gut has its own nervous system — calming it matters.'
  },
  {
    icon: Dumbbell,
    title: 'Regular Movement',
    content: 'Moderate exercise (yoga, walking, cycling) reduces IBS symptoms. Yoga specifically targets gut motility and stress response — RCTs show significant improvements. Even a 20-minute walk after meals aids digestion. Avoid intense exercise during flares.'
  },
  {
    icon: Activity,
    title: 'Meal Hygiene',
    content: 'Eat at regular times, chew thoroughly (20+ chews per bite), eat slowly, and avoid eating while stressed or distracted. Smaller, more frequent meals often work better than large meals. Avoid drinking large amounts of liquid with meals (can dilute digestive enzymes).'
  },
  {
    icon: Shield,
    title: 'Trigger Identification',
    content: 'Keep a food-symptom diary for 2-4 weeks to identify YOUR triggers. Common non-food triggers: stress, poor sleep, menstrual cycle, travel, and antibiotics. The Monash University FODMAP app is the gold standard for food tracking in IBS.'
  }
];

const EvidenceGrade = ({ grade }) => {
  const colors = {
    'A': 'bg-green-100 text-green-800 border-green-300',
    'B+': 'bg-blue-100 text-blue-800 border-blue-300',
    'B': 'bg-sky-100 text-sky-800 border-sky-300'
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-bold rounded-full border ${colors[grade] || colors['B']}`} data-testid={`evidence-grade-${grade}`}>
      Evidence: {grade}
    </span>
  );
};

export default function IBSGuidePage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-primary" data-testid="ibs-guide-back">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="small" linkTo="/" />
          <div className="w-16" />
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full" data-testid="ibs-guide-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <Activity className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-3">
            IBS & Gut Health Guide
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto text-sm sm:text-base">
            An evidence-based supplement protocol for Irritable Bowel Syndrome — covering gut healing, microbiome restoration, and symptom management for all IBS subtypes.
          </p>
          <div className="flex gap-2 justify-center mt-4">
            <span className="px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200">IBS-D</span>
            <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200">IBS-C</span>
            <span className="px-3 py-1 rounded-full bg-purple-50 text-purple-700 text-xs font-medium border border-purple-200">IBS-M</span>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-8" data-testid="ibs-guide-disclaimer">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-900 mb-1">Important Medical Disclaimer</p>
              <p className="text-xs text-amber-800">
                This guide is for educational purposes only. If you haven't been formally diagnosed with IBS, please see a gastroenterologist first to rule out celiac disease, IBD, and other conditions.
                Always discuss supplement changes with your healthcare provider.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-10">
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
            <Pill className="w-5 h-5 text-primary" />
            IBS Supplement Protocol
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            8 evidence-based supplements for gut health. Start with probiotics and peppermint oil (strongest evidence), then add based on your IBS subtype.
          </p>

          <div className="bg-white rounded-2xl border border-border p-5 mb-6" data-testid="daily-schedule">
            <h3 className="font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" />
              Optimized Daily Schedule
            </h3>
            <div className="space-y-3">
              {[
                { time: 'Morning (empty stomach)', supps: 'Probiotics (30 min before food), L-Glutamine (in water)', color: 'bg-amber-50 border-amber-200 text-amber-900' },
                { time: 'Before breakfast', supps: 'Peppermint Oil (enteric-coated, 30 min before), Ginger Extract', color: 'bg-sky-50 border-sky-200 text-sky-900' },
                { time: 'With meals', supps: 'Digestive Enzymes (start of each meal), Psyllium Fiber (with water), Vitamin D3 (breakfast)', color: 'bg-orange-50 border-orange-200 text-orange-900' },
                { time: 'Evening', supps: 'Peppermint Oil (before dinner), Magnesium (before bed), Ginger Extract (before dinner)', color: 'bg-indigo-50 border-indigo-200 text-indigo-900' }
              ].map((slot, i) => (
                <div key={i} className={`rounded-xl border p-3 ${slot.color}`}>
                  <p className="text-xs font-semibold">{slot.time}</p>
                  <p className="text-xs mt-1">{slot.supps}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <Accordion type="multiple" className="space-y-3">
              {IBS_PROTOCOL.map((supp, index) => {
                const Icon = supp.icon;
                return (
                  <motion.div key={supp.name} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * index }}>
                    <AccordionItem value={supp.name} className="bg-white rounded-xl border border-border overflow-hidden">
                      <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-gray-50/50">
                        <div className="flex items-center gap-3 text-left">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${supp.color}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-sm text-[#1A1A1A]">{supp.name}</h3>
                              <EvidenceGrade grade={supp.evidence} />
                            </div>
                            <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                              <span>{supp.dosage}</span>
                              <span>{supp.timing}</span>
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <div className="space-y-4 pt-2">
                          <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                            <h4 className="text-sm font-medium text-emerald-900 mb-1 flex items-center gap-2">
                              <FlaskConical className="w-4 h-4" />
                              Why This Matters for IBS
                            </h4>
                            <p className="text-xs text-emerald-800">{supp.why}</p>
                          </div>
                          {supp.interactions.length > 0 && (
                            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                              <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
                                <Shield className="w-4 h-4" />
                                Medication Interactions
                              </h4>
                              <ul className="space-y-1">
                                {supp.interactions.map((int_, i) => (
                                  <li key={i} className="text-xs text-blue-800 flex items-start gap-2">
                                    <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                    {int_}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {supp.notes && (
                            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                              <h4 className="text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
                                <Info className="w-4 h-4" />
                                Clinical Note
                              </h4>
                              <p className="text-xs text-gray-700">{supp.notes}</p>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>
                );
              })}
            </Accordion>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-10">
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-primary" />
            Medication Compatibility Guide
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            How these supplements interact with common IBS medications.
          </p>
          <div className="space-y-3" data-testid="medication-compatibility">
            {COMMON_MEDICATIONS.map((med, i) => (
              <div key={i} className="bg-white rounded-xl border border-border p-4">
                <h4 className="font-semibold text-sm text-[#1A1A1A] mb-1">{med.name}</h4>
                <p className="text-xs text-muted-foreground">{med.notes}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-10">
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
            <Heart className="w-5 h-5 text-primary" />
            IBS Lifestyle Strategies
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            Evidence-based lifestyle modifications that complement your supplement protocol.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="lifestyle-tips">
            {LIFESTYLE_TIPS.map((tip, i) => {
              const Icon = tip.icon;
              return (
                <div key={i} className="bg-white rounded-xl border border-border p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <h3 className="font-semibold text-sm text-[#1A1A1A]">{tip.title}</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{tip.content}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-6 mb-10 text-center" data-testid="symptom-tracker-cta">
          <h3 className="text-lg font-bold text-emerald-900 mb-2">Track Your Gut Health</h3>
          <p className="text-sm text-emerald-700 mb-4 max-w-md mx-auto">
            Log symptoms, food triggers, bowel habits, and stress levels daily to identify patterns and share with your gastroenterologist.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="btn-primary">
              <Link to="/symptoms">Start Symptom Tracking</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/journal">Open Journal</Link>
            </Button>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="grid grid-cols-2 gap-3 mb-10">
          <Link to="/quiz" className="bg-white rounded-xl border border-border p-4 text-center hover:border-primary/40 transition-colors">
            <Pill className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-sm font-medium text-[#1A1A1A]">Get Personalized Protocol</p>
            <p className="text-xs text-muted-foreground mt-1">Take the full assessment</p>
          </Link>
          <Link to="/ask" className="bg-white rounded-xl border border-border p-4 text-center hover:border-primary/40 transition-colors">
            <Brain className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-sm font-medium text-[#1A1A1A]">Ask AI About IBS</p>
            <p className="text-xs text-muted-foreground mt-1">Get supplement answers</p>
          </Link>
        </motion.div>
      </main>

      <GlobalFooter />
    </div>
  );
}
